import { Fragment, useEffect, useState } from "react"

// ** Third Party Components
import { SearchNormal1 } from "iconsax-reactjs"
import { useMutation } from "@tanstack/react-query"

// ** Services
import { pinOrUnpinProjectService } from "@pma/services/project"
import { getGlobalSearchFilterService } from "@pma/services/common"

// ** Constants
import { defaultGlobalSearchData, defaultGlobalSearchPagination } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import GlobalSearchDialog from "./GlobalSearchDialog"
import GlobalSearchPopover from "./GlobalSearchPopover"

interface GlobalSearchProps {
  isMobile?: boolean
}

const GlobalSearch = ({ isMobile = false }: GlobalSearchProps) => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const orgId = authConfig?.last_organization_id

  const [searchText, setSearchText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [resetState, setResetState] = useState(false)
  const [isApiCalling, setIsApiCalling] = useState(false)
  const [activeTab, setActiveTab] = useState<string>("all")
  const [isMoreFetching, setIsMoreFetching] = useState(false)
  const [projectDetails, setProjectDetails] = useState<any>(null)
  const [globalSearchDialog, setGlobalSearchDialog] = useState(false)
  const [globalSearchData, setGlobalSearchData] = useState<any>(defaultGlobalSearchData)
  const [paginationData, setPaginationData] = useState<any>(defaultGlobalSearchPagination)

  const getSearchFiltersByTab = () => {
    switch (activeTab) {
      case "file": // Files
        return { in_files: 1, in_projects: 0, in_notes: 0, in_tasks: 0 }
      case "project": // Projects
        return { in_files: 0, in_projects: 1, in_notes: 0, in_tasks: 0 }
      default: // All
        return { in_files: 1, in_projects: 1, in_notes: 1, in_tasks: 1 }
    }
  }

  const handleGlobalSearchList = async (currentPage: number) => {
    const filters = getSearchFiltersByTab()

    const payload: any = {
      paging_start_page: currentPage,
      paging_per_page: 25,
      organization_id: orgId,
      ...filters,
    }

    if (searchText) payload["search_text"] = searchText

    await getGlobalSearchFilterService(payload)
      .then((response) => {
        setIsLoading(false)
        setIsMoreFetching(false)
        setGlobalSearchData((pre: any) => ({
          ...pre,
          [activeTab]: {
            data: isMoreFetching ? [...pre[activeTab].data, ...response?.items] : response?.items,
            counter: response.itemsTotal,
          },
        }))
        setIsApiCalling(false)
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (isApiCalling && searchText) {
      setIsLoading(true)
      handleGlobalSearchList(paginationData[activeTab])
    } else if (searchText.length == 0) {
      setIsApiCalling(false)
      setGlobalSearchData((pre: any) => ({ ...pre, [activeTab]: { data: [], counter: 0 } }))
    }
  }, [isApiCalling])

  useEffect(() => {
    if (isMoreFetching) handleGlobalSearchList(paginationData[activeTab])
  }, [isMoreFetching])

  useEffect(() => {
    if (resetState) {
      setResetState(false)
      setSearchText("")
      setActiveTab("all")
      setIsApiCalling(false)
      setGlobalSearchData(defaultGlobalSearchData)
      setPaginationData(defaultGlobalSearchPagination)
    }
  }, [resetState])

  const pinnedOrUnpinnedProjectMutation = useMutation({
    mutationKey: ["pin-or-unpinned-project"],
    mutationFn: pinOrUnpinProjectService,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: `${projectDetails?.title} ${translate(data.pinned ? "has been pinned" : "has been unpinned")}`,
        variant: "success",
      })
      setProjectDetails(null)
      setIsLoading(true)
      handleGlobalSearchList(paginationData[activeTab])
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to pin")} ${projectDetails?.title}`,
        variant: "error",
      })
    },
  })

  useEffect(() => {
    if (projectDetails) pinnedOrUnpinnedProjectMutation.mutate(projectDetails.id)
  }, [projectDetails])

  return (
    <Fragment>
      {isMobile ? (
        <div className='flex flex-col gap-1'>
          <SearchNormal1 onClick={() => setGlobalSearchDialog(true)} />
        </div>
      ) : (
        <div className='relative flex gap-2 rounded-full'>
          <GlobalSearchPopover
            isLoading={isLoading}
            activeTab={activeTab}
            searchText={searchText}
            setActiveTab={setActiveTab}
            setResetState={setResetState}
            setSearchText={setSearchText}
            isMoreFetching={isMoreFetching}
            setIsApiCalling={setIsApiCalling}
            globalSearchData={globalSearchData}
            setPaginationData={setPaginationData}
            setIsMoreFetching={setIsMoreFetching}
            setProjectDetails={setProjectDetails}
          />
        </div>
      )}

      {globalSearchDialog && (
        <GlobalSearchDialog
          isLoading={isLoading}
          activeTab={activeTab}
          searchText={searchText}
          setActiveTab={setActiveTab}
          setResetState={setResetState}
          setSearchText={setSearchText}
          isMoreFetching={isMoreFetching}
          setIsApiCalling={setIsApiCalling}
          globalSearchData={globalSearchData}
          setPaginationData={setPaginationData}
          setIsMoreFetching={setIsMoreFetching}
          setProjectDetails={setProjectDetails}
          globalSearchDialog={globalSearchDialog}
          setGlobalSearchDialog={setGlobalSearchDialog}
        />
      )}
    </Fragment>
  )
}

export default GlobalSearch
